import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArchiveIcon, PrinterIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useMachines } from '@/api/machines'
import type { Machine } from '@/api/machines'
import { useWorkspace } from '@/api/workspace'
import { FilterBar } from '@/components/filter-bar'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { MachineBadges } from '@/components/machines/machine-badges'
import { formatWhole, rateMissingReason } from '@/components/machines/machine-format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'

// Machines list (UI-SCREENS F-01, #127): name, model, printing (with the Batch number, #128) /
// idle, the Machine rate ("—" while an input is missing, 08a #202) and the archived badge behind the Archived
// toggle. New Machine is Owner only; *Start batch* on an idle Machine opens F-05.
//
// The Archived filter sits in the shared filter bar (as on I-03) and lives in the URL.
const searchSchema = z.object({
  archived: z.boolean().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/machines/')({
  component: MachinesList,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'machines.title' },
})

function MachinesList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const workspace = useWorkspace(slug)
  const { archived = false } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const machines = useMachines(slug, { archived })

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('machines.title')}</h1>
        {workspace.data?.role === 'owner' ? (
          <Button asChild className="h-11">
            <Link to="/w/$slug/machines/new" params={{ slug }}>
              {t('machines.newMachine')}
            </Link>
          </Button>
        ) : null}
      </div>

      <FilterBar
        fields={[
          {
            key: 'archived',
            label: t('filterBar.archived'),
            icon: ArchiveIcon,
            value: archived ? 'true' : undefined,
            options: [{ value: 'true', label: t('filterBar.shown') }],
            onChange: (value) => navigate({ search: value === 'true' ? { archived: true } : {}, replace: true }),
          },
        ]}
        onClearAll={() => navigate({ search: {}, replace: true })}
      />

      {machines.isPending ? (
        <LoadingSkeleton />
      ) : machines.isError || !machines.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : machines.data.length === 0 ? (
        <RecordListEmpty>{t('machines.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {machines.data.map((machine) => (
            <MachineRow key={machine.id} slug={slug} machine={machine} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

// The Machine rate is the row's figure, whole Rp for display; "—" with the reason while an input is
// missing (08a #202).
function MachineRow({ slug, machine }: { slug: string; machine: Machine }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      title={
        <RecordLink to="/w/$slug/machines/$machineId" params={{ slug, machineId: String(machine.id) }}>
          {machine.name}
        </RecordLink>
      }
      badges={<MachineBadges machine={machine} />}
      meta={[machine.model ? { icon: PrinterIcon, label: t('machines.detail.model'), value: machine.model } : null]}
      description={machine.hourly_rate === null ? rateMissingReason(t, machine.rate_missing) : undefined}
      figure={machine.hourly_rate === null ? '—' : t('machines.list.rate', { value: formatWhole(machine.hourly_rate) })}
      caption={t('machines.detail.hourlyRate')}
      actions={
        machine.status === 'idle' && !machine.archived_at ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/w/$slug/batches/new" params={{ slug }} search={{ machineId: machine.id }}>
              {t('batches.start')}
            </Link>
          </Button>
        ) : null
      }
    />
  )
}
